<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { ReportGameResponse, WalletTransactionType } from "@frontend/walletapi";
  import { GetCachedToken, me } from "@frontend/user";
  import { service } from '@frontend/services';
  import { formatMoney } from './Formats';
  import { groups } from "@frontend/grouplist";
  import { push } from "svelte-spa-router";
  
  let games: ReportGameResponse[] = [];
  let error: string = '';
  let startDate: string = '';
  let endDate: string = '';
  let loading: boolean = false;
  let totalDebit: number = 0;
  let totalCredit: number = 0;
  let currentGroupId: number = 0;
  let hasAccess: boolean = false;

  function isParentOrAncestor(parentId: number, childId: number, groupsList: any[]): boolean {
    const child = groupsList.find(g => g.Id === childId);
    if (!child) return false;
    if (childId === parentId) return true;
    if (child.ParentId === parentId) return true;
    if (child.ParentId) {
      return isParentOrAncestor(parentId, child.ParentId, groupsList);
    }
    return false;
  }

  async function checkAccess(): Promise<boolean> {
    const meData = get(me);
    if (!meData?.GroupId) {
      error = 'No se encontró el GroupId del usuario';
      return false;
    }

    const groupsList = get(groups);
    if (!groupsList || groupsList.length === 0) {
      error = 'No se pudo obtener la información de grupos';
      return false;
    }

    currentGroupId = Number(meData.GroupId);

    const userGroup = groupsList.find(g => g.Id === currentGroupId);
    if (!userGroup) {
      error = 'Grupo no encontrado';
      return false;
    }

    hasAccess = true;
    console.log('Verificación de acceso:', {
      userGroupId: currentGroupId,
      parentId: userGroup.ParentId
    });

    return true;
  }

  function filterGamesByGroup(games: ReportGameResponse[]): ReportGameResponse[] {
    const groupsList = get(groups);
    
    return games.filter(game => {
      if (!game.GroupId) return false;

      return game.GroupId === currentGroupId || 
             isParentOrAncestor(currentGroupId, game.GroupId, groupsList) ||
             isParentOrAncestor(game.GroupId, currentGroupId, groupsList);
    });
  }

  async function getAllGames() {
    try {
      if (!await checkAccess()) {
        push('/'); 
        return;
      }

      loading = true;
      const groupsList = get(groups);
      
      const relatedGroups = groupsList.filter(g => 
        g.Id === currentGroupId || 
        g.ParentId === currentGroupId || 
        g.Id === groupsList.find(pg => pg.Id === currentGroupId)?.ParentId 
      ).map(g => g.Id);

      console.log('Grupos incluidos en la búsqueda:', relatedGroups);

      const start = new Date(2020, 0, 1); 
      const end = new Date();
      end.setHours(23, 59, 59);

      console.log('Buscando juegos entre fechas:', { 
        start: start.toISOString(), 
        end: end.toISOString(),
        groupId: currentGroupId,
        relatedGroups
      });
      
      const response = await service.walletapi.reportGame({
        Token: GetCachedToken(),
        Data: {
          Datestart: start,
          Dateend: end,
          TransactionType: undefined,
          PlayerId: undefined,
          GameId: undefined,
          UserId: undefined,
          PlayerSessionId: undefined,
          GroupId: currentGroupId
        }
      });

      if (response.Success || response.ErrorMessage === 'No se encontraron transacciones.') {
        games = filterGamesByGroup(response.Data || []); // Filtrar los juegos aquí
        calculateTotals(games);
        error = '';
      } else {
        error = `Error al obtener juegos: ${response.ErrorMessage}`;
        console.error(error);
        games = [];
      }
    } catch (err) {
      error = `Error inesperado: ${err}`;
      console.error('Error al obtener juegos:', err);
      games = [];
    } finally {
      loading = false;
    }
  }

  function calculateTotals(games: ReportGameResponse[]) {
    totalDebit = games
      .filter(game => game.Type === WalletTransactionType.Debit)
      .reduce((sum, game) => sum + (game.CashableAmount?.InCents || 0), 0);

    totalCredit = games
      .filter(game => game.Type === WalletTransactionType.Credit)
      .reduce((sum, game) => sum + (game.CashableAmount?.InCents || 0), 0);
  }

  function calculateGameStats(games: ReportGameResponse[]) {
    const gameTransactions = games.filter(game => 
      game.GameId !== undefined && 
      game.GameId !== null && 
      game.GameName !== undefined && 
      game.GameName !== null &&
      game.GameName.trim() !== ''
    );

    const gameStats = gameTransactions.reduce((stats, game) => {
      const gameKey = `${game.GameId}-${game.GameName}`;
      if (!stats[gameKey]) {
        stats[gameKey] = {
          id: game.GameId,
          name: game.GameName,
          code: game.GameCode,
          count: 0,
          players: new Set(),
          transactions: [] as ReportGameResponse[]
        };
      }
      stats[gameKey].count++;
      if (game.PlayerId) {
        stats[gameKey].players.add(game.PlayerId);
      }
      stats[gameKey].transactions.push(game);
      return stats;
    }, {} as Record<string, {
      id: number | undefined;
      name: string | undefined;
      code: string | undefined;
      count: number;
      players: Set<number>;
      transactions: ReportGameResponse[];
    }>);

    return Object.values(gameStats)
      .sort((a, b) => b.count - a.count)
      .map(stat => {
        const debitAmount = stat.transactions
          .filter(t => t.Type === WalletTransactionType.Debit)
          .reduce((sum, t) => sum + (t.CashableAmount?.InCents || 0), 0);
        
        const creditAmount = stat.transactions
          .filter(t => t.Type === WalletTransactionType.Credit)
          .reduce((sum, t) => sum + (t.CashableAmount?.InCents || 0), 0);

        const balance = debitAmount - creditAmount;

        return {
          ...stat,
          uniquePlayers: stat.players.size,
          debitAmount,
          creditAmount,
          balance,
          transactions: undefined 
        };
      });
  }

  function getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  async function searchByDateRange() {
    if (!startDate || !endDate) {
      error = 'Por favor seleccione ambas fechas';
      return;
    }

    try {
      if (!await checkAccess()) {
        push('/'); 
        return;
      }

      loading = true;
      const groupsList = get(groups);
      
      const relatedGroups = groupsList.filter(g => 
        g.Id === currentGroupId || 
        g.ParentId === currentGroupId || 
        g.Id === groupsList.find(pg => pg.Id === currentGroupId)?.ParentId 
      ).map(g => g.Id);

      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59);

      if (start > end) {
        error = 'La fecha inicial debe ser menor o igual a la fecha final';
        return;
      }

      console.log('Buscando juegos entre fechas:', { 
        start: start.toISOString(), 
        end: end.toISOString(),
        groupId: currentGroupId,
        relatedGroups
      });
      
      const response = await service.walletapi.reportGame({
        Token: GetCachedToken(),
        Data: {
          Datestart: start,
          Dateend: end,
          TransactionType: undefined,
          PlayerId: undefined,
          GameId: undefined,
          UserId: undefined,
          PlayerSessionId: undefined,
          GroupId: currentGroupId
        }
      });

      if (response.Success || response.ErrorMessage === 'No se encontraron transacciones.') {
        games = filterGamesByGroup(response.Data || []); 
        calculateTotals(games);
        error = '';
      } else {
        error = `Error al obtener juegos: ${response.ErrorMessage}`;
        console.error(error);
        games = [];
      }
    } catch (err) {
      error = `Error inesperado: ${err}`;
      console.error('Error al buscar juegos:', err);
      games = [];
    } finally {
      loading = false;
    }
  }

  function clearSearch() {
    startDate = '';
    endDate = '';
    getAllGames();
  }

  onMount(async () => {
    // Verificar autenticación primero
    if (!$me) {
      push("/user/Login");
      return;
    }

    await getAllGames();
  });

  me.subscribe(async (userData) => {
    if (userData?.GroupId && userData.GroupId !== currentGroupId) {
      await getAllGames();
    }
  });
</script>

{#if !hasAccess}
  <div class="ui negative message">
    <p>No tienes acceso a esta sección.</p>
  </div>
{:else}
  <div class="ui container">
    {#if error}
      <div class="ui error message">
        <p>{error}</p>
      </div>
    {/if}
    
    {#if games.length > 0}
      <div class="ui segment">
        <div class="ui form">
          <div class="fields">
            <div class="three wide field">
              <label>Fecha Inicial</label>
              <input 
                type="date" 
                bind:value={startDate}
                max={endDate || undefined}
                class="medium-input"
              >
            </div>
            <div class="three wide field">
              <label>Fecha Final</label>
              <input 
                type="date" 
                bind:value={endDate}
                min={startDate || undefined}
                class="medium-input"
              >
            </div>
            <div class="four wide field">
              <label>Débitos</label>
              <div class="ui labeled input">
                <div class="ui red label">$</div>
                <input 
                  type="text" 
                  readonly 
                  value={formatMoney({ InCents: totalDebit })}
                  class="readonly medium-input"
                >
              </div>
            </div>
            <div class="four wide field">
              <label>Créditos</label>
              <div class="ui labeled input">
                <div class="ui green label">$</div>
                <input 
                  type="text" 
                  readonly 
                  value={formatMoney({ InCents: totalCredit })}
                  class="readonly medium-input"
                >
              </div>
            </div>
            <div class="two wide field" style="display: flex; align-items: flex-end;">
              <div class="ui small buttons">
                <button 
                  class="ui small primary button" 
                  on:click={searchByDateRange}
                  disabled={loading || (!startDate && !endDate)}>
                  {#if loading}
                    <i class="spinner loading icon"></i>
                  {:else}
                    <i class="search icon"></i>
                  {/if}
                </button>
                {#if startDate || endDate}
                  <button class="ui small button" on:click={clearSearch} disabled={loading}>
                    <i class="eraser icon"></i>
                  </button>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="ui segment">
        <h3>Estadísticas por Juego</h3>
        <div class="ui info message">
          <p>
            <i class="info circle icon"></i>
            El balance se calcula como: Débitos - Créditos
          </p>
        </div>
        <table class="ui celled structured table">
          <thead>
            <tr>
              <th rowspan="2">Juego</th>
              <th rowspan="2">Código</th>
              <th colspan="5" class="center aligned">Estadísticas</th>
            </tr>
            <tr>
              <th class="center aligned">Veces Jugado</th>
              <th class="center aligned">Jugadores Únicos</th>
              <th class="center aligned">Total Débitos</th>
              <th class="center aligned">Total Créditos</th>
              <th class="center aligned">Balance (D-C)</th>
            </tr>
          </thead>
          <tbody>
            {#each calculateGameStats(games) as stat}
              <tr>
                <td>{stat.name}</td>
                <td>{stat.code || 'N/A'}</td>
                <td class="center aligned">
                  <div class="ui blue circular label">
                    {stat.count}
                  </div>
                </td>
                <td class="center aligned">
                  <div class="ui teal circular label">
                    {stat.uniquePlayers}
                  </div>
                </td>
                <td class="right aligned negative">
                  {formatMoney({ InCents: stat.debitAmount })}
                </td>
                <td class="right aligned positive">
                  {formatMoney({ InCents: stat.creditAmount })}
                </td>
                <td class="right aligned {stat.balance > 0 ? 'negative' : 'positive'}">
                  {formatMoney({ InCents: stat.balance })}
                </td>
              </tr>
            {/each}
          </tbody>
          <tfoot>
            <tr>
              <th colspan="4" class="right aligned">Totales:</th>
              <th class="right aligned negative">
                {formatMoney({ InCents: calculateGameStats(games).reduce((sum, stat) => sum + stat.debitAmount, 0) })}
              </th>
              <th class="right aligned positive">
                {formatMoney({ InCents: calculateGameStats(games).reduce((sum, stat) => sum + stat.creditAmount, 0) })}
              </th>
              <th class="right aligned {calculateGameStats(games).reduce((sum, stat) => sum + stat.balance, 0) > 0 ? 'negative' : 'positive'}">
                {formatMoney({ InCents: calculateGameStats(games).reduce((sum, stat) => sum + stat.balance, 0) })}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    {:else}
      <div class="ui warning message">
        <p>No hay datos disponibles para mostrar en este grupo.</p>
      </div>
    {/if}
  </div>
{/if}

<style>
  .ui.container {
    margin-top: 1em;
  }
  
  .ui.form .field {
    margin-bottom: 0.8em;
  }

  .ui.button {
    margin-left: 0.3em;
  }

  .medium-input {
    padding: 0.5em !important;
    font-size: 1em !important;
    height: 2.5em !important;
  }

  input[type="date"].medium-input {
    padding: 0.3em !important;
    width: 100%;
  }

  input.readonly {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }

  .ui.labeled.input {
    width: 100%;
  }

  .ui.small.buttons {
    display: flex;
    gap: 0.3em;
  }

  .ui.table td.negative {
    background-color: #fff6f6 !important;
    color: #9f3a38 !important;
  }

  .ui.table td.positive {
    background-color: #fcfff5 !important;
    color: #2c662d !important;
  }

  .ui.circular.label {
    min-width: 2.5em;
  }

  .center.aligned {
    text-align: center !important;
  }

  .right.aligned {
    text-align: right !important;
  }
</style>
